import React, { Component } from 'react'
import ReactDOM from 'react-dom'

const SCROLLER_EL_ID = 'scrollerEl'

type Props = {
  onScroll: { up: () => void; down: () => void }
  onReach: { top: () => void; bottom: () => void; offset: number }
  reachEls: {
    elId: string
    top: () => void
    bottom: () => void
    offset: number
  }[]
  permanently: boolean
}

export default class Scroller extends Component<Props> {
  constructor(props: Props) {
    super(props)

    this.ticking = false
    this.prevVal = 0
    this.currentVal = 0

    this.reachEls = {}

    this[SCROLLER_EL_ID] = {
      onScrollUpCalled: false,
      onScrollDownCalled: false,
      onReachTopCalled: false,
      onReachBottomCalled: false,
    }
  }

  componentDidMount(): void {
    window.addEventListener('scroll', this.handleScroll)
  }

  componentWillUnmount(): void {
    window.removeEventListener('scroll', this.handleScroll)
    window.cancelAnimationFrame(this.rafId)
  }

  getEl(): Element | Text | null {
    return ReactDOM.findDOMNode(this)
  }

  getElRect(elId: number): DOMRect {
    const el = this[elId].el || this.getEl()

    return el.getBoundingClientRect()
  }

  handleScroll = (): void => {
    this.prevVal = this.currentVal
    this.currentVal = window.pageYOffset

    if (this.ticking) {
      return
    }

    this.rafId = window.requestAnimationFrame(() => {
      this.onScroll()
      this.onReach()
      this.onReachEls()

      this.ticking = false
    })

    this.ticking = true
  }

  onScroll(): void {
    const { onScroll: { up, down } = {} } = this.props

    if (this.isDown()) {
      if (
        typeof down === 'function' &&
        (this.isPermanently() || !this.onScrollDownCalled)
      ) {
        this.onScrollDownCalled = true
        this.onScrollUpCalled = false

        down(this.currentVal)
      }
    } else if (
      typeof up === 'function' &&
      (this.isPermanently() || !this.onScrollUpCalled)
    ) {
      this.onScrollUpCalled = true
      this.onScrollDownCalled = false

      up(this.currentVal)
    }
  }

  onReach({ elId = SCROLLER_EL_ID, onReach } = {}): void {
    onReach = onReach || this.props.onReach || {}

    const { top, bottom, offset = 0 } = onReach

    if (this.isDown()) {
      if (
        typeof top === 'function' &&
        (this.isPermanently() || !this[elId].onReachTopCalled) &&
        this.getElRect(elId).top - offset <= 0
      ) {
        this[elId].onReachTopCalled = true
        this[elId].onReachBottomCalled = false

        top()
      }
    } else if (
      typeof bottom === 'function' &&
      (this.isPermanently() || !this[elId].onReachBottomCalled) &&
      this.getElRect(elId).top - offset >= 0
    ) {
      this[elId].onReachBottomCalled = true
      this[elId].onReachTopCalled = false

      bottom()
    }
  }

  onReachEls(): void {
    const { reachEls = [] } = this.props

    reachEls.forEach(reachEl => {
      const { elId, onReach } = reachEl

      if (!this[elId]) {
        this[elId] = { el: window.document.getElementById(elId) }
      }

      if (!this[elId].el) {
        return
      }

      this.onReach({ elId, onReach })
    })
  }

  isDown(): boolean {
    return this.currentVal > this.prevVal
  }

  isPermanently(): boolean {
    const { permanently = false } = this.props

    return permanently
  }

  render(): JSX.Element {
    return <div className="scroller">{this.props.children}</div>
  }
}
